import React, { useState, useMemo, useEffect } from 'react';
import { collection, addDoc, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getEventSettings, subscribeToEventSettings } from '../utils/settingsService';
import '../App.css';

function RegistrationPage() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    gender: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [eventSettings, setEventSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);

  useEffect(() => {
    // Fetch settings immediately on mount so we only ever show Firestore data
    const loadSettings = async () => {
      try {
        const settings = await getEventSettings();
        setEventSettings(settings);
      } catch (error) {
        console.error('Error loading event settings:', error);
        // On error, keep eventSettings as null so we don't show stale hardcoded values
      } finally {
        setSettingsLoading(false);
      }
    };

    loadSettings();

    // Then subscribe to real-time updates
    const unsubscribe = subscribeToEventSettings((settings) => {
      setEventSettings(settings);
      setSettingsLoading(false);
    });

    // Cleanup subscription on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Calculate pricing (Now Free)
  const pricing = useMemo(() => {
    return {
      isEarlyBird: false,
      amount: 0,
      display: 'Free',
      displayShort: 'Free'
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');

    try {
      // Check if email already exists
      const registrationsRef = collection(db, 'registrations');
      const q = query(registrationsRef, where('email', '==', formData.email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        setSubmitError('This email is already registered');
        setSubmitting(false);
        return;
      }

      // Save registration to Firestore
      await addDoc(collection(db, 'registrations'), {
        fullName: formData.fullName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        gender: formData.gender,
        registeredAt: serverTimestamp(),
        status: 'paid', // Automatically mark as paid since it's free
        paidAt: serverTimestamp(),
        amount: 0,
        pricingType: 'free'
      });

      // WhatsApp registration redirect logic
      const phoneNumber = '2348051973437'; // +234 805 197 3437
      const message = `Hi! I've just registered for the MTU Alumni Association event (OLD SKOOL HANGOUT WITH THE TRIBE).\n\nName: ${formData.fullName}\nEmail: ${formData.email}\nPhone: ${formData.phoneNumber}\nGender: ${formData.gender}\nRegistration: Free`;
      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
    } catch (error) {
      console.error('Error submitting registration:', error);
      setSubmitError('Failed to submit registration. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="app">
      <div className="body-container">
        <div className="main-content">
          <div className="card-container">
            <div className="card">
              {/* Left Side - Event Info */}
              <div className="event-info-section">
                <div className="event-info-overlay"></div>
                <div className="event-info-content">
                  {/* Logo */}
                  <div className="logo-container">
                    <div className="logo-circle">
                      <img
                        src={process.env.PUBLIC_URL + '/logo.jpg'}
                        alt="Mountain Top University"
                        className="logo-image"
                      />
                    </div>
                  </div>

                  {/* Title */}
                  <div className="event-title-section">
                    <h1 className="event-title">MTU Alumni Association</h1>
                    <h2 className="event-subtitle">{eventSettings?.eventName || ''}</h2>
                  </div>

                  {/* Description */}
                  <div className="event-description">
                    <p>
                      Join us for a fun and nostalgic Old Skool Hangout with your MTU family! Open to all MTU Alumni and current 300 & 400 level students, this event promises music, networking, great vibes, and unforgettable memories.
                    </p>
                  </div>

                  {/* Event Details - only render after settings have loaded */}
                  {!settingsLoading && eventSettings && (
                    <div className="event-details">
                      <div className="detail-item">
                        <div className="detail-icon">
                          <CalendarIcon />
                        </div>
                        <span className="detail-text">Date : {eventSettings.eventDate}</span>
                      </div>
                      <div className="detail-item">
                        <div className="detail-icon">
                          <ClockIcon />
                        </div>
                        <span className="detail-text">Time : {eventSettings.eventTime}</span>
                      </div>
                      <div className="detail-item">
                        <div className="detail-icon">
                          <LocationIcon />
                        </div>
                        <span className="detail-text">Location : {eventSettings.eventLocation}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Side - Registration Form */}
              <div className="registration-section">
                <div className="registration-content">
                  <div className="form-header">
                    <h2 className="form-title">Register for Event</h2>
                    <p className="form-subtitle">Fill in your details to secure your spot</p>
                  </div>

                  <form onSubmit={handleSubmit} className="registration-form">
                    {/* Full Name Field */}
                    <div className="form-field">
                      <label htmlFor="fullName">
                        Full Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        placeholder="Enter your full name"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    {/* Email Field */}
                    <div className="form-field">
                      <label htmlFor="email">
                        Email Address <span className="required">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        placeholder="Enter your email address"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    {/* Phone Number Field */}
                    <div className="form-field">
                      <label htmlFor="phoneNumber">
                        Phone Number <span className="required">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phoneNumber"
                        name="phoneNumber"
                        placeholder="Enter your phone number"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    {/* Gender Field */}
                    <div className="form-field">
                      <label htmlFor="gender">
                        Gender <span className="required">*</span>
                      </label>
                      <select
                        id="gender"
                        name="gender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        required
                        className="form-select"
                      >
                        <option value="">Select gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>

                    {/* Registration Fee */}
                    <div className="fee-container">
                      <div className="fee-content">
                        <div className="fee-left">
                          <span className="fee-label">Registration Fee:</span>
                          {pricing.isEarlyBird && (
                            <span className="early-bird-badge">Early Bird</span>
                          )}
                        </div>
                        <span className="fee-amount">{pricing.display}</span>
                      </div>
                    </div>

                    {/* Error Message */}
                    {submitError && (
                      <div style={{
                        background: '#FEE2E2',
                        border: '1px solid #FCA5A5',
                        color: '#DC2626',
                        padding: '12px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        textAlign: 'center'
                      }}>
                        {submitError}
                      </div>
                    )}

                    {/* Submit Button */}
                    <button type="submit" className="submit-button" disabled={submitting}>
                      <WhatsAppIcon />
                      <span>{submitting ? 'Submitting...' : 'Register Now'}</span>
                    </button>

                    {/* Payment Notice */}
                    <div className="payment-notice">
                      <InfoIcon />
                      <p>You will be redirected to WhatsApp to confirm your registration</p>
                    </div>

                    {/* Security Footer */}
                    <div className="security-footer">
                      <LockIcon />
                      <span>Data protected and secure</span>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Icon Components
const CalendarIcon = () => (
  <svg viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2H11V0H9V2H5V0H3V2H2C0.9 2 0 2.9 0 4V14C0 15.1 0.9 16 2 16H12C13.1 16 14 15.1 14 14V4C14 2.9 13.1 2 12 2ZM12 14H2V7H12V14Z" fill="#F096F7" />
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 0C3.58 0 0 3.58 0 8C0 12.42 3.58 16 8 16C12.42 16 16 12.42 16 8C16 3.58 12.42 0 8 0ZM8 14C4.69 14 2 11.31 2 8C2 4.69 4.69 2 8 2C11.31 2 14 4.69 14 8C14 11.31 11.31 14 8 14ZM8.5 4H7V9L11.5 11.5L12.25 10.25L8.5 8.25V4Z" fill="#F096F7" />
  </svg>
);

const LocationIcon = () => (
  <svg viewBox="0 0 12 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 0C2.69 0 0 2.69 0 6C0 9.5 6 16 6 16C6 16 12 9.5 12 6C12 2.69 9.31 0 6 0ZM6 8C4.9 8 4 7.1 4 6C4 4.9 4.9 4 6 4C7.1 4 8 4.9 8 6C8 7.1 7.1 8 6 8Z" fill="#F096F7" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 0C4.03 0 0 3.9 0 8.7C0 10.92 0.84 12.96 2.25 14.55L0.75 19.25L5.7 17.8C7.17 18.53 8.55 18.7 9 18.7C13.97 18.7 18 14.8 18 10C18 5.2 13.97 1.3 9 1.3V0ZM9 16.8C8.52 16.8 7.47 16.63 5.7 15.9L5.25 15.7L2.4 16.5L3.2 13.75L2.95 13.25C2.17 11.68 1.65 9.75 1.65 8.7C1.65 4.85 4.97 1.75 9 1.75C13.03 1.75 16.35 4.85 16.35 8.7C16.35 12.55 13.03 15.65 9 15.65V16.8Z" fill="white" />
  </svg>
);

const InfoIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 0C3.14 0 0 3.14 0 7C0 10.86 3.14 14 7 14C10.86 14 14 10.86 14 7C14 3.14 10.86 0 7 0ZM7.5 10.5H6.5V6.5H7.5V10.5ZM7.5 5.5H6.5V4.5H7.5V5.5Z" fill="#6B7381" />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 0C5.34 0 4 1.34 4 3V4H3C2.45 4 2 4.45 2 5V12C2 12.55 2.45 13 3 13H11C11.55 13 12 12.55 12 12V5C12 4.45 11.55 4 11 4H10V3C10 1.34 8.66 0 7 0ZM7 1.5C8.1 1.5 9 2.4 9 3.5V4H5V3.5C5 2.4 5.9 1.5 7 1.5ZM3 5H11V12H3V5ZM7 8C7.55 8 8 8.45 8 9C8 9.55 7.55 10 7 10C6.45 10 6 9.55 6 9C6 8.45 6.45 8 7 8Z" fill="#6B7381" />
  </svg>
);

export default RegistrationPage;

